/**
 * MilestonesSection — Project Detail's "Milestones" collapsible catalog:
 * add form, table (with per-outcome assignment counts), and edit/delete.
 * Extracted from `routes/projects/$projectId.tsx` (issue #1411).
 */
import { useState } from 'react';
import {
  createProjectMilestone,
  ApiError,
  type CreateProjectMilestoneInput,
  type ProjectMilestoneStatusInput,
} from '../../lib/apiClient';
import { StatusBadge, PermissionGate, CollapsibleSection } from '../shared';
import type { MilestoneOutcomeCounts } from '../../lib/projectDashboard';
import type { ProjectMilestone } from '../../graphql/types';

const ForgeButton = 'forge-button' as React.ElementType;
const IconElement = 'forge-icon' as React.ElementType;
const BannerElement = 'forge-banner' as React.ElementType;

export interface MilestonesSectionProps {
  projectId: string;
  milestones: ProjectMilestone[];
  outcomeCountsByMilestoneId: Map<string, MilestoneOutcomeCounts>;
  milestoneInUse: (ms: ProjectMilestone) => boolean;
  canManageProject: boolean;
  onMilestoneAdded: () => void;
  onEditMilestone: (milestone: ProjectMilestone) => void;
  onRequestDeleteMilestone: (milestone: ProjectMilestone) => void;
}

/** Add-milestone form + catalog table for the Project Detail page. */
export function MilestonesSection({
  projectId,
  milestones,
  outcomeCountsByMilestoneId,
  milestoneInUse,
  canManageProject,
  onMilestoneAdded,
  onEditMilestone,
  onRequestDeleteMilestone,
}: MilestonesSectionProps): JSX.Element {
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState<{
    name: string;
    sequence: string;
    status: ProjectMilestoneStatusInput;
    targetDate: string;
    description: string;
  }>({
    name: '',
    sequence: '',
    status: 'not-started',
    targetDate: '',
    description: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddMilestone = async (): Promise<void> => {
    if (!milestoneForm.name.trim()) {
      setFormError('Milestone name is required');
      return;
    }
    setIsSubmitting(true);
    setFormError(null);
    try {
      const input: CreateProjectMilestoneInput = {
        projectId,
        name: milestoneForm.name.trim(),
        sequence: Number(milestoneForm.sequence) || milestones.length + 1,
        status: milestoneForm.status,
        targetDate: milestoneForm.targetDate || undefined,
        description: milestoneForm.description || undefined,
      };
      await createProjectMilestone(input);
      setMilestoneForm({
        name: '',
        sequence: '',
        status: 'not-started',
        targetDate: '',
        description: '',
      });
      setShowMilestoneForm(false);
      onMilestoneAdded();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to add milestone');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="project-detail-section" data-testid="milestones-section">
      <CollapsibleSection
        headingId="milestones-heading"
        title="Milestones"
        testId="milestones"
        actions={
          <PermissionGate permission="project:update">
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() => {
                setShowMilestoneForm(true);
                setFormError(null);
              }}
            >
              Add Milestone
            </ForgeButton>
          </PermissionGate>
        }
      >
        {formError && showMilestoneForm && (
          <BannerElement theme="error" dismissed={false} role="alert">
            <span>{formError}</span>
          </BannerElement>
        )}

        {showMilestoneForm && (
          <div className="inline-form" data-testid="milestone-form">
            <div className="inline-form-field">
              <label className="forge-typography--caption" htmlFor="ms-name">
                Name *
              </label>
              <input
                id="ms-name"
                className="search-input"
                value={milestoneForm.name}
                onChange={(e) => setMilestoneForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Milestone name"
                aria-label="Milestone name"
              />
            </div>
            <div className="inline-form-field">
              <label className="forge-typography--caption" htmlFor="ms-seq">
                Sequence
              </label>
              <input
                id="ms-seq"
                type="number"
                className="search-input milestone-sequence-input"
                value={milestoneForm.sequence}
                onChange={(e) => setMilestoneForm((p) => ({ ...p, sequence: e.target.value }))}
                placeholder={String(milestones.length + 1)}
                aria-label="Milestone sequence"
              />
            </div>
            <div className="inline-form-field">
              <label className="forge-typography--caption" htmlFor="ms-status">
                Status
              </label>
              <select
                id="ms-status"
                className="filter-select"
                value={milestoneForm.status}
                onChange={(e) =>
                  setMilestoneForm((p) => ({
                    ...p,
                    status: e.target.value as ProjectMilestoneStatusInput,
                  }))
                }
                aria-label="Milestone status"
              >
                <option value="not-started">Not Started</option>
                <option value="in-progress">In Progress</option>
                <option value="complete">Complete</option>
                <option value="slipped">Slipped</option>
              </select>
            </div>
            <div className="inline-form-field">
              <label className="forge-typography--caption" htmlFor="ms-date">
                Target Date
              </label>
              <input
                id="ms-date"
                type="date"
                className="search-input"
                value={milestoneForm.targetDate}
                onChange={(e) => setMilestoneForm((p) => ({ ...p, targetDate: e.target.value }))}
                aria-label="Milestone target date"
              />
            </div>
            <ForgeButton
              type="button"
              variant="raised"
              disabled={isSubmitting}
              onClick={() => void handleAddMilestone()}
            >
              Save
            </ForgeButton>
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() => setShowMilestoneForm(false)}
            >
              Cancel
            </ForgeButton>
          </div>
        )}

        {milestones.length === 0 && !showMilestoneForm && (
          <p className="forge-typography--body2 text-secondary">No milestones defined yet.</p>
        )}
        {milestones.length > 0 && (
          <table className="project-detail-table" aria-label="Milestones">
            <thead>
              <tr>
                <th scope="col" className="project-detail-table-col-seq">
                  Seq
                </th>
                <th scope="col">Name</th>
                <th scope="col">Status</th>
                <th scope="col">Target Date</th>
                <th scope="col">Assigned</th>
                {canManageProject && (
                  <th scope="col" className="project-detail-table-col-actions">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {milestones.map((ms) => {
                const outcomeCounts = outcomeCountsByMilestoneId.get(ms.entityId);
                const inUse = milestoneInUse(ms);
                return (
                  <tr key={ms.entityId} data-testid={`milestone-${ms.entityId}`}>
                    <td className="forge-typography--caption project-detail-table-col-seq">
                      {ms.sequence}
                    </td>
                    <td className="forge-typography--body2">{ms.name}</td>
                    <td>
                      <StatusBadge value={ms.status} variant="lifecycle" />
                    </td>
                    <td className="forge-typography--caption text-secondary">
                      {ms.targetDate ?? '—'}
                    </td>
                    <td
                      className="forge-typography--caption milestone-outcome-counts"
                      data-testid={`milestone-${ms.entityId}-outcome-counts`}
                    >
                      {!outcomeCounts || outcomeCounts.total === 0 ? (
                        <span className="milestone-outcome-counts-empty">No services assigned</span>
                      ) : (
                        <>
                          <span className="milestone-outcome-counts-total">
                            {outcomeCounts.total} assigned
                          </span>
                          {outcomeCounts.breakdown.length > 0 && (
                            <span className="milestone-outcome-counts-breakdown">
                              {' '}
                              (
                              {outcomeCounts.breakdown
                                .map((entry) => `${entry.outcome.name}: ${entry.count}`)
                                .join(', ')}
                              )
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    {canManageProject && (
                      <td className="project-detail-table-actions">
                        <ForgeButton
                          type="button"
                          variant="text"
                          onClick={() => onEditMilestone(ms)}
                          aria-label={`Edit milestone ${ms.name}`}
                          data-testid={`edit-milestone-${ms.entityId}`}
                        >
                          <IconElement name="edit" slot="start" aria-hidden="true" />
                          Edit
                        </ForgeButton>
                        <ForgeButton
                          type="button"
                          variant="text"
                          className="action-btn--danger"
                          disabled={inUse}
                          onClick={() => onRequestDeleteMilestone(ms)}
                          aria-label={`Delete milestone ${ms.name}`}
                          title={
                            inUse
                              ? 'Cannot delete: milestone has one or more assigned services'
                              : undefined
                          }
                          data-testid={`delete-milestone-${ms.entityId}`}
                        >
                          <IconElement name="delete" slot="start" aria-hidden="true" />
                          Delete
                        </ForgeButton>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CollapsibleSection>
    </section>
  );
}
